import { Modal } from "@peer-start/ui";
import { Play } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

type VimeoTimeUpdate = { seconds: number };
type VimeoPlayer = {
  off: (event: string, callback: (event: VimeoTimeUpdate) => void) => void;
  on: (event: string, callback: (event: VimeoTimeUpdate) => void) => void;
  pause: () => Promise<void>;
  setCurrentTime: (seconds: number) => Promise<number>;
};
type VimeoPlayerApi = { Player: new (iframe: HTMLIFrameElement) => VimeoPlayer };

declare global {
  interface Window {
    Vimeo?: VimeoPlayerApi;
  }
}

let vimeoApiPromise: Promise<VimeoPlayerApi> | null = null;

function loadVimeoApi(): Promise<VimeoPlayerApi> {
  if (window.Vimeo) return Promise.resolve(window.Vimeo);
  if (vimeoApiPromise) return vimeoApiPromise;
  vimeoApiPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[src="https://player.vimeo.com/api/player.js"]',
    );
    const script = existing ?? document.createElement("script");
    const loaded = () => (window.Vimeo ? resolve(window.Vimeo) : reject(new Error("Vimeo API unavailable")));
    script.addEventListener("load", loaded, { once: true });
    script.addEventListener("error", () => reject(new Error("Vimeo API failed to load")), {
      once: true,
    });
    if (!existing) {
      script.src = "https://player.vimeo.com/api/player.js";
      document.head.appendChild(script);
    }
  });
  return vimeoApiPromise;
}

function vimeoEmbedUrl(
  videoId: string,
  autoplay: boolean,
  startSeconds?: number,
  endSeconds?: number,
): string {
  const parameters = `badge=0&autopause=0&player_id=0&app_id=58479${autoplay ? "&autoplay=1" : "&controls=0"}`;
  const segment =
    startSeconds === undefined
      ? ""
      : `#t=${startSeconds}s${endSeconds === undefined ? "" : `&end=${endSeconds}s`}`;
  return `https://player.vimeo.com/video/${videoId}?${parameters}${segment}`;
}

function timeLabel(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

export function VimeoVideo({
  videoId,
  thumbnailSrc,
  startSeconds,
  endSeconds,
  title,
  controlId,
  compact = false,
}: {
  videoId: string;
  thumbnailSrc?: string;
  startSeconds?: number;
  endSeconds?: number;
  title: string;
  controlId: string;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const iframe = useRef<HTMLIFrameElement>(null);
  const clipped = startSeconds !== undefined && endSeconds !== undefined;

  useEffect(() => {
    if (!open || !iframe.current || !clipped) return;
    let disposed = false;
    let player: VimeoPlayer | null = null;
    let resetting = false;
    const keepInsideClip = (event: VimeoTimeUpdate) => {
      if (!player || resetting) return;
      if (event.seconds >= endSeconds || event.seconds < startSeconds - 0.5) {
        resetting = true;
        void player
          .pause()
          .then(() => player?.setCurrentTime(startSeconds))
          .finally(() => {
            resetting = false;
          });
      }
    };
    void loadVimeoApi()
      .then((api) => {
        if (disposed || !iframe.current) return;
        player = new api.Player(iframe.current);
        player.on("timeupdate", keepInsideClip);
        player.on("seeking", keepInsideClip);
      })
      .catch(() => undefined);
    return () => {
      disposed = true;
      player?.off("timeupdate", keepInsideClip);
      player?.off("seeking", keepInsideClip);
    };
  }, [clipped, endSeconds, open, startSeconds]);

  const clipLabel = clipped
    ? t("passportVig.lesson.microlearning.range", {
        start: timeLabel(startSeconds),
        end: timeLabel(endSeconds),
      })
    : null;
  const thumbnail = (
    <div className="relative aspect-video overflow-hidden rounded-xl bg-muted">
      {thumbnailSrc ? (
        <img
          alt=""
          aria-hidden="true"
          className="absolute inset-0 size-full object-cover"
          loading="lazy"
          src={thumbnailSrc}
        />
      ) : (
        <iframe
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 size-full border-0"
          loading="lazy"
          referrerPolicy="strict-origin-when-cross-origin"
          src={vimeoEmbedUrl(videoId, false, startSeconds, endSeconds)}
          tabIndex={-1}
          title=""
        />
      )}
      <div aria-hidden="true" className="absolute inset-0 flex items-center justify-center bg-foreground/10">
        <span
          className={`flex items-center justify-center rounded-full bg-background/95 text-foreground shadow-md ${compact ? "size-9" : "size-14"}`}
        >
          <Play className={`${compact ? "size-4" : "size-6"} ml-0.5 fill-current`} />
        </span>
      </div>
      {!compact && clipped ? (
        <span className="absolute left-2 top-2 rounded-full bg-background/95 px-2.5 py-1 text-xs font-medium text-foreground shadow-sm">
          {t("passportVig.lesson.microlearning.badge", { count: endSeconds - startSeconds })}
        </span>
      ) : null}
    </div>
  );

  if (compact) return thumbnail;

  return (
    <>
      <button
        aria-label={t("passportVig.lesson.playTitle", { title })}
        className="group block w-full max-w-sm rounded-xl text-left focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring"
        data-control-id={`${controlId}.open`}
        onClick={() => setOpen(true)}
        type="button"
      >
        {thumbnail}
        {clipLabel ? <span className="mt-2 block text-xs text-muted-foreground">{clipLabel}</span> : null}
        <span className="mt-2 block text-sm font-medium text-foreground group-hover:text-link">
          {t("passportVig.lesson.play")}
        </span>
      </button>
      <Modal
        closeLabel={t("passportVig.lesson.closeVideo")}
        controlId={controlId}
        onClose={() => setOpen(false)}
        open={open}
        size="wide"
        title={t("passportVig.lesson.videoTitle", { title })}
      >
        <div className="relative aspect-video overflow-hidden rounded-xl bg-muted">
          <iframe
            allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
            allowFullScreen
            className="absolute inset-0 size-full border-0"
            ref={iframe}
            referrerPolicy="strict-origin-when-cross-origin"
            src={vimeoEmbedUrl(videoId, true, startSeconds, endSeconds)}
            title={title}
          />
        </div>
        {clipLabel ? <p className="mt-3 text-sm font-medium text-muted-foreground">{clipLabel}</p> : null}
      </Modal>
    </>
  );
}
